import { jsPDF } from 'jspdf';
import { format } from 'date-fns';

// Color constants (RGB)
const BRAND_COLOR: [number, number, number] = [139, 69, 19]; // Sienna brown
const TEXT_COLOR: [number, number, number] = [51, 51, 51];
const MUTED_COLOR: [number, number, number] = [102, 102, 102];
const LIGHT_BG: [number, number, number] = [249, 249, 249];
const BORDER_COLOR: [number, number, number] = [221, 221, 221];

// Helper to add page if needed
function checkPageBreak(pdf: jsPDF, y: number, margin: number, needed: number = 20): number {
  const pageHeight = pdf.internal.pageSize.getHeight();
  if (y + needed > pageHeight - margin) {
    pdf.addPage();
    return margin;
  }
  return y;
}

// Draw a horizontal line
function drawLine(pdf: jsPDF, x1: number, y: number, x2: number, color: [number, number, number] = BORDER_COLOR) {
  pdf.setDrawColor(...color);
  pdf.setLineWidth(0.5);
  pdf.line(x1, y, x2, y);
}

// Draw section header
function drawSectionHeader(pdf: jsPDF, title: string, y: number, margin: number, pageWidth: number): number {
  pdf.setFontSize(14);
  pdf.setTextColor(...BRAND_COLOR);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, y);
  drawLine(pdf, margin, y + 2, pageWidth - margin, BORDER_COLOR);
  return y + 10;
}

// Wrap text helper
function wrapText(pdf: jsPDF, text: string, maxWidth: number): string[] {
  return pdf.splitTextToSize(text, maxWidth);
}

interface AgreementData {
  contractNumber: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  events: Array<{
    date: string;
    occasion: string;
    startTime: string;
    endTime: string;
    venueAddress: string;
    guestCount?: number;
    packageName?: string;
    packagePrice?: number;
    instruments?: string[];
  }>;
  totalAmount: number;
  depositAmount: number;
  depositDueDate?: string;
  paymentMode?: 'full' | 'deposit';
  balanceAmount?: number;
  balanceDueDate?: string;
  balanceReminderDate?: string;
  dailyLateFee?: number;
  depositPercentage?: number;
  inclusions: string[];
  exclusions: string[];
  paymentTerms: string;
  cancellationPolicy: string;
  forceMajeureClause: string;
  liabilityClause: string;
  specialRequirements?: string;
  additionalNotes?: string;
}

export function generateAgreementPdf(data: AgreementData): Blob {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Header
  pdf.setFontSize(24);
  pdf.setTextColor(...BRAND_COLOR);
  pdf.setFont('helvetica', 'bold');
  pdf.text('THE LOST STRINGS COMPANY', pageWidth / 2, y, { align: 'center' });
  y += 8;

  pdf.setFontSize(12);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Live Music Services Agreement', pageWidth / 2, y, { align: 'center' });
  y += 6;

  pdf.setFontSize(10);
  pdf.text(`Contract #: ${data.contractNumber}`, pageWidth / 2, y, { align: 'center' });
  y += 4;

  drawLine(pdf, margin, y, pageWidth - margin, BRAND_COLOR);
  y += 12;

  // Client Information
  y = drawSectionHeader(pdf, 'Client Information', y, margin, pageWidth);
  
  pdf.setFontSize(10);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFont('helvetica', 'normal');

  const clientInfo = [
    ['Name:', data.clientName],
    ['Email:', data.clientEmail],
    ['Phone:', data.clientPhone],
    ['Contract Date:', format(new Date(), 'MMMM d, yyyy')],
  ];

  clientInfo.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, margin, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, margin + 30, y);
    y += 6;
  });
  y += 6;

  // Event Details
  y = checkPageBreak(pdf, y, margin, 40);
  y = drawSectionHeader(pdf, 'Event Details', y, margin, pageWidth);

  data.events.forEach((event, idx) => {
    // Calculate dynamic height based on content
    const hasPackage = !!event.packageName;
    const hasInstruments = event.instruments && event.instruments.length > 0;
    let boxHeight = 22; // Base height for date, time, venue
    if (hasPackage) boxHeight += 6;
    if (hasInstruments) {
      pdf.setFontSize(9);
      const instrumentsText = `Ensemble: ${event.instruments!.join(', ')}`;
      const wrapped = wrapText(pdf, instrumentsText, contentWidth - 12);
      boxHeight += wrapped.length * 4 + 2;
    }
    
    y = checkPageBreak(pdf, y, margin, boxHeight + 10);
    
    // Event box background
    pdf.setFillColor(...LIGHT_BG);
    pdf.roundedRect(margin, y - 2, contentWidth, boxHeight, 2, 2, 'F');
    pdf.setDrawColor(...BRAND_COLOR);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y - 2, margin, y + boxHeight - 2);
    
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND_COLOR);
    pdf.text(`Event ${idx + 1}: ${event.occasion}`, margin + 4, y + 4);
    
    pdf.setFontSize(9);
    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Date: ${event.date}`, margin + 4, y + 10);
    pdf.text(`Time: ${event.startTime} - ${event.endTime}`, margin + 60, y + 10);
    
    // Truncate venue if too long
    const venueText = event.venueAddress.length > 60 
      ? event.venueAddress.substring(0, 57) + '...' 
      : event.venueAddress;
    pdf.text(`Venue: ${venueText}`, margin + 4, y + 16);
    
    let contentY = y + 22;
    
    if (hasPackage) {
      const priceText = event.packagePrice ? ` - $${event.packagePrice.toLocaleString()}` : '';
      pdf.text(`Package: ${event.packageName}${priceText}`, margin + 4, contentY);
      contentY += 6;
    }
    
    if (hasInstruments) {
      const instrumentsText = `Ensemble: ${event.instruments!.join(', ')}`;
      const wrapped = wrapText(pdf, instrumentsText, contentWidth - 12);
      wrapped.forEach((line, i) => {
        pdf.text(line, margin + 4, contentY + (i * 4));
      });
    }
    
    y += boxHeight + 6;
  });

  // Total / payment summary
  const depositDueLabel = data.depositDueDate
    ? format(new Date(`${data.depositDueDate}T00:00:00`), 'MMMM d, yyyy')
    : 'Upon signing';
  const voidDate = data.depositDueDate ? new Date(`${data.depositDueDate}T00:00:00`) : null;
  if (voidDate) voidDate.setDate(voidDate.getDate() + 1);
  const voidDateLabel = voidDate ? format(voidDate, 'MMMM d, yyyy') : depositDueLabel;
  const balanceDueLabel = data.balanceDueDate
    ? format(new Date(`${data.balanceDueDate}T00:00:00`), 'MMMM d, yyyy')
    : 'TBD';
  const effectiveDailyLateFee = data.paymentMode === 'deposit' ? Math.max(data.dailyLateFee || 0, 20) : 0;
  const summaryBoxHeight = data.paymentMode === 'deposit'
    ? 46
    : 12;
  y = checkPageBreak(pdf, y, margin, summaryBoxHeight + 8);
  pdf.setFillColor(...LIGHT_BG);
  pdf.setDrawColor(...BRAND_COLOR);
  pdf.setLineWidth(1);
  pdf.roundedRect(margin, y, contentWidth, summaryBoxHeight, 2, 2, 'FD');

  if (data.paymentMode === 'deposit') {
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND_COLOR);
    pdf.text(`Total Contract Value: $${data.totalAmount.toLocaleString()}`, pageWidth - margin - 4, y + 7, { align: 'right' });

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...TEXT_COLOR);
    const depositSummary = data.depositPercentage
      ? `Initial Deposit at Signature: $${data.depositAmount.toLocaleString()} (${data.depositPercentage}%)`
      : `Initial Deposit at Signature: $${data.depositAmount.toLocaleString()}`;
    const balanceSummary = `Remaining Balance: $${(data.balanceAmount || 0).toLocaleString()} due ${balanceDueLabel}`;
    pdf.text(depositSummary, margin + 4, y + 14);
    pdf.text(`Initial Deposit Due: ${depositDueLabel}`, margin + 4, y + 19);
    pdf.text(balanceSummary, margin + 4, y + 24);
    pdf.text(`Agreement Void Date: ${voidDateLabel} if the initial deposit is unpaid`, margin + 4, y + 29);

    pdf.text(`Late Fee: $${effectiveDailyLateFee.toLocaleString()} per day after the balance due date`, margin + 4, y + 34);
  } else {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND_COLOR);
    pdf.text(`Total Contract Value: $${data.totalAmount.toLocaleString()}`, pageWidth - margin - 4, y + 8, { align: 'right' });
  }
  y += summaryBoxHeight + 8;

  // Inclusions/Exclusions
  y = checkPageBreak(pdf, y, margin, 30);
  y = drawSectionHeader(pdf, "What's Included / Not Included", y, margin, pageWidth);

  const colWidth = contentWidth / 2 - 5;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Included:', margin, y);
  pdf.text('Not Included (Client Responsibility):', margin + colWidth + 10, y);
  y += 6;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);

  const maxItems = Math.max(data.inclusions.length, data.exclusions.length);
  for (let i = 0; i < maxItems; i++) {
    y = checkPageBreak(pdf, y, margin, 6);
    if (data.inclusions[i]) {
      // Green bullet for inclusions
      pdf.setFillColor(40, 167, 69);
      pdf.circle(margin + 2, y - 1.5, 1.5, 'F');
      pdf.setTextColor(...TEXT_COLOR);
      pdf.text(data.inclusions[i], margin + 7, y);
    }
    if (data.exclusions[i]) {
      // Red bullet for exclusions
      pdf.setFillColor(220, 53, 69);
      pdf.circle(margin + colWidth + 12, y - 1.5, 1.5, 'F');
      pdf.setTextColor(...TEXT_COLOR);
      pdf.text(data.exclusions[i], margin + colWidth + 17, y);
    }
    y += 5;
  }
  y += 8;

  // Payment Schedule
  y = checkPageBreak(pdf, y, margin, 60);
  y = drawSectionHeader(pdf, 'Payment Schedule', y, margin, pageWidth);
  
  const isDeposit = data.paymentMode === 'deposit';

  if (isDeposit) {
    // Deposit + Balance layout
    // Background box
    const boxHeight = 52 + (data.dailyLateFee && data.dailyLateFee > 0 ? 12 : 0);
    pdf.setFillColor(...LIGHT_BG);
    pdf.setDrawColor(...BRAND_COLOR);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(margin, y - 4, contentWidth, boxHeight, 2, 2, 'FD');

    const col1 = margin + 5;
    const col2 = margin + contentWidth / 2 + 5;

    // Row 1: Deposit
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text('Deposit Amount', col1, y + 2);
    pdf.text('Deposit Due Date', col2, y + 2);
    y += 6;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...TEXT_COLOR);
    const depositLabel = data.depositPercentage 
      ? `$${data.depositAmount.toLocaleString()} (${data.depositPercentage}%)`
      : `$${data.depositAmount.toLocaleString()}`;
    pdf.text(depositLabel, col1, y + 2);
    pdf.text(depositDueLabel, col2, y + 2);
    y += 10;

    // Divider
    drawLine(pdf, col1, y, margin + contentWidth - 5, BORDER_COLOR);
    y += 6;

    // Row 2: Balance
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text('Remaining Balance', col1, y + 2);
    pdf.text('Balance Due Date', col2, y + 2);
    y += 6;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(`$${(data.balanceAmount || 0).toLocaleString()}`, col1, y + 2);
    pdf.text(balanceDueLabel, col2, y + 2);
    y += 10;

    // Divider
    drawLine(pdf, col1, y, margin + contentWidth - 5, BORDER_COLOR);
    y += 6;

    // Row 3: Total
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text('Total Contract Value', col1, y + 2);
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND_COLOR);
    pdf.text(`$${data.totalAmount.toLocaleString()}`, col2, y + 2);
    y += 10;

    // Late fee note
    if (data.dailyLateFee && data.dailyLateFee > 0) {
      y += 2;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(...MUTED_COLOR);
      pdf.text(`A late fee of $${data.dailyLateFee} per day will be applied to payments received after the balance due date.`, col1, y);
      y += 6;
    }

    y += 4;

    // Deposit terms note
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(`If the initial deposit is not received by ${depositDueLabel}, this agreement becomes void on ${voidDateLabel}.`, margin, y);
    y += 8;
  } else {
    // Full payment layout
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(`Payment Amount: $${data.totalAmount.toLocaleString()}`, margin, y);
    if (data.depositDueDate) {
      pdf.text(`Due Date: ${format(new Date(data.depositDueDate), 'MMMM d, yyyy')}`, margin + 80, y);
    }
    y += 8;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text('One-time payment for the full amount required to confirm booking.', margin, y);
    y += 8;
  }

  // Terms sections
  const termsSections = [
    { title: 'Payment Terms', content: data.paymentTerms },
    { title: 'Cancellation & Refund Policy', content: data.cancellationPolicy },
    { title: 'Force Majeure', content: data.forceMajeureClause },
    { title: 'Liability & Insurance', content: data.liabilityClause },
  ];

  if (data.specialRequirements) {
    termsSections.push({ title: 'Special Requirements', content: data.specialRequirements });
  }
  if (data.additionalNotes) {
    termsSections.push({ title: 'Additional Notes', content: data.additionalNotes });
  }

  termsSections.forEach(section => {
    y = checkPageBreak(pdf, y, margin, 20);
    y = drawSectionHeader(pdf, section.title, y, margin, pageWidth);
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...TEXT_COLOR);
    
    const lines = wrapText(pdf, section.content, contentWidth);
    lines.forEach(line => {
      y = checkPageBreak(pdf, y, margin, 5);
      pdf.text(line, margin, y);
      y += 4;
    });
    y += 6;
  });

  // Signatures
  y = checkPageBreak(pdf, y, margin, 50);
  y += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Client Signature', margin, y);
  pdf.text('The Lost Strings Company', margin + colWidth + 20, y);
  y += 8;

  pdf.setFont('helvetica', 'normal');
  pdf.text(`Name: ${data.clientName}`, margin, y);
  pdf.text('Name: _________________', margin + colWidth + 20, y);
  y += 6;
  pdf.text('Date: _________________', margin, y);
  pdf.text('Date: _________________', margin + colWidth + 20, y);
  y += 6;
  pdf.text('Signature: _________________', margin, y);
  pdf.text('Signature: _________________', margin + colWidth + 20, y);

  // Footer
  y = pdf.internal.pageSize.getHeight() - 15;
  drawLine(pdf, margin, y - 5, pageWidth - margin);
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.text('The Lost Strings Company | hello@theloststrings.com | www.theloststrings.com', pageWidth / 2, y, { align: 'center' });

  return pdf.output('blob');
}

interface TechRiderData {
  documentNumber: string;
  eventName: string;
  eventDate: string;
  venue?: string;
  instruments: Array<{
    name: string;
    position: string;
    zone: string;
    microphone: string;
    microphoneModel?: string;
    diRequired: boolean;
    powerOutlets: number;
    monitor?: string;
    stageNotes?: string;
    roamingNotes?: string;
  }>;
  totalChannels: number;
  totalPowerOutlets: number;
  generalRequirements: string[];
  stageplotNotes: string[];
  setupNotes?: string;
}

export function generateTechRiderPdf(data: TechRiderData): Blob {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Header
  pdf.setFontSize(20);
  pdf.setTextColor(...BRAND_COLOR);
  pdf.setFont('helvetica', 'bold');
  pdf.text('TECHNICAL RIDER', pageWidth / 2, y, { align: 'center' });
  y += 7;

  pdf.setFontSize(11);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.setFont('helvetica', 'normal');
  pdf.text('The Lost Strings Company', pageWidth / 2, y, { align: 'center' });
  y += 4;

  drawLine(pdf, margin, y, pageWidth - margin, BRAND_COLOR);
  y += 10;

  // Meta info box
  pdf.setFillColor(...LIGHT_BG);
  pdf.roundedRect(margin, y, contentWidth, 20, 2, 2, 'F');
  
  pdf.setFontSize(9);
  pdf.setTextColor(...TEXT_COLOR);
  
  const metaY = y + 6;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Event:', margin + 4, metaY);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.eventName, margin + 18, metaY);

  pdf.setFont('helvetica', 'bold');
  pdf.text('Date:', margin + 80, metaY);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.eventDate, margin + 92, metaY);

  if (data.venue) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Venue:', margin + 4, metaY + 7);
    pdf.setFont('helvetica', 'normal');
    const venueText = wrapText(pdf, data.venue, 70);
    pdf.text(venueText[0], margin + 18, metaY + 7);
  }

  pdf.setFont('helvetica', 'bold');
  pdf.text('Musicians:', margin + 80, metaY + 7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.instruments.length.toString(), margin + 102, metaY + 7);

  y += 26;

  // Summary badges - using text without emojis
  pdf.setFillColor(...BRAND_COLOR);
  const badges = [
    `${data.totalChannels} Channels`,
    `${data.totalPowerOutlets} Power Outlets`,
    `${data.instruments.length} Monitor Mixes`,
  ];
  
  let badgeX = margin;
  pdf.setFontSize(8);
  badges.forEach(badge => {
    const badgeWidth = pdf.getTextWidth(badge) + 8;
    pdf.roundedRect(badgeX, y, badgeWidth, 7, 1, 1, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.text(badge, badgeX + 4, y + 5);
    badgeX += badgeWidth + 4;
  });
  pdf.setTextColor(...TEXT_COLOR);
  y += 14;

  // Instrument Requirements Table
  y = drawSectionHeader(pdf, 'Instrument Requirements', y, margin, pageWidth);
  
  // Table header
  const colWidths = [35, 30, 40, 25, 40];
  const headers = ['Instrument', 'Position', 'Microphone', 'Monitor', 'Notes'];
  
  pdf.setFillColor(26, 26, 26);
  pdf.rect(margin, y, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  
  let colX = margin + 2;
  headers.forEach((header, i) => {
    pdf.text(header, colX, y + 5);
    colX += colWidths[i];
  });
  y += 8;

  // Table rows
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);

  data.instruments.forEach((inst, idx) => {
    y = checkPageBreak(pdf, y, margin, 10);
    
    if (idx % 2 === 0) {
      pdf.setFillColor(...LIGHT_BG);
      pdf.rect(margin, y, contentWidth, 8, 'F');
    }
    
    colX = margin + 2;
    pdf.setFont('helvetica', 'bold');
    pdf.text(inst.name, colX, y + 5);
    colX += colWidths[0];
    
    pdf.setFont('helvetica', 'normal');
    pdf.text(inst.position, colX, y + 5);
    colX += colWidths[1];
    
    const micText = inst.diRequired ? `${inst.microphone} + DI` : inst.microphone;
    pdf.text(micText.substring(0, 20), colX, y + 5);
    colX += colWidths[2];
    
    pdf.text(inst.monitor || '-', colX, y + 5);
    colX += colWidths[3];
    
    pdf.text((inst.stageNotes || '-').substring(0, 25), colX, y + 5);
    
    y += 8;
  });

  // Laptop requirement
  y += 4;
  pdf.setFillColor(255, 243, 205);
  pdf.roundedRect(margin, y, contentWidth, 10, 2, 2, 'F');
  pdf.setTextColor(...BRAND_COLOR);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('* Regional Manager Laptop', margin + 4, y + 6);
  pdf.setFont('helvetica', 'normal');
  pdf.text(' - Stereo DI (Radial ProD2) + 3.5mm to 1/4" TRS AUX cable', margin + 50, y + 6);
  y += 16;

  // Setup Notes
  if (data.setupNotes) {
    y = checkPageBreak(pdf, y, margin, 25);
    pdf.setFillColor(255, 243, 205);
    pdf.setDrawColor(255, 193, 7);
    pdf.roundedRect(margin, y, contentWidth, 20, 2, 2, 'FD');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Additional Setup Notes:', margin + 4, y + 6);
    pdf.setFont('helvetica', 'normal');
    const setupLines = wrapText(pdf, data.setupNotes, contentWidth - 8);
    setupLines.slice(0, 3).forEach((line, i) => {
      pdf.text(line, margin + 4, y + 12 + (i * 4));
    });
    y += 26;
  }

  // General Requirements
  y = checkPageBreak(pdf, y, margin, 20);
  y = drawSectionHeader(pdf, 'General Requirements', y, margin, pageWidth);
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  data.generalRequirements.forEach(req => {
    y = checkPageBreak(pdf, y, margin, 5);
    pdf.setFillColor(...BRAND_COLOR);
    pdf.circle(margin + 4, y - 1, 1, 'F');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(req, margin + 8, y);
    y += 5;
  });
  y += 6;

  // Stage Setup Notes
  y = checkPageBreak(pdf, y, margin, 20);
  y = drawSectionHeader(pdf, 'Stage Setup Notes', y, margin, pageWidth);
  
  data.stageplotNotes.forEach(note => {
    y = checkPageBreak(pdf, y, margin, 5);
    pdf.setFillColor(...BRAND_COLOR);
    pdf.circle(margin + 4, y - 1, 1, 'F');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(note, margin + 8, y);
    y += 5;
  });

  // Footer
  y = pdf.internal.pageSize.getHeight() - 12;
  drawLine(pdf, margin, y - 3, pageWidth - margin);
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.text(`Generated by The Lost Strings Company CRM - ${format(new Date(), 'MMMM d, yyyy')}`, pageWidth / 2, y, { align: 'center' });

  return pdf.output('blob');
}

interface TimelineEvent {
  time: string;
  description: string;
  isHighlight?: boolean;
  isMuted?: boolean;
}

interface TravelItineraryData {
  documentNumber: string;
  clientName: string;
  originCode: string;
  trips: Array<{
    originCode: string;
    destinationCode: string;
    destinationName: string;
    flightDuration: string;
    departureDate: string;
    departureTime: string;
    landingDate: string;
    landingTime: string;
    destinationTimezone: string;
    returnDate: string;
    returnTime: string;
    isRedEye?: boolean;
    eventOccasion: string;
    eventTime: string;
    setupTime: string;
    timeline: TimelineEvent[];
  }>;
}

export function generateTimelinePdf(data: TravelItineraryData): Blob {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Header
  pdf.setFontSize(22);
  pdf.setTextColor(...BRAND_COLOR);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Travel Itinerary', pageWidth / 2, y, { align: 'center' });
  y += 8;

  pdf.setFontSize(11);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${data.clientName} | Origin: ${data.originCode}`, pageWidth / 2, y, { align: 'center' });
  y += 4;

  drawLine(pdf, margin, y, pageWidth - margin, BRAND_COLOR);
  y += 10;

  // Trips
  data.trips.forEach((trip, tripIdx) => {
    y = checkPageBreak(pdf, y, margin, 60);
    
    // Flight summary card
    pdf.setFillColor(245, 240, 235);
    pdf.setDrawColor(...BRAND_COLOR);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(margin, y, contentWidth, 25, 3, 3, 'FD');
    
    // Route - plain text, no special characters
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND_COLOR);
    const routeText = `${trip.originCode} to ${trip.destinationCode}`;
    pdf.text(routeText, margin + 5, y + 8);
    
    // Duration badge - calculate position based on route text width
    const routeWidth = pdf.getTextWidth(routeText);
    const badgeStartX = margin + 10 + routeWidth;
    
    pdf.setFillColor(...MUTED_COLOR);
    const durationBadge = `${trip.flightDuration}`;
    pdf.setFontSize(8);
    const durationWidth = pdf.getTextWidth(durationBadge) + 6;
    pdf.roundedRect(badgeStartX, y + 3, durationWidth, 7, 1, 1, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.text(durationBadge, badgeStartX + 3, y + 8);
    
    if (trip.isRedEye) {
      pdf.setFillColor(217, 119, 6);
      const redEyeText = 'Red-eye';
      const redEyeWidth = pdf.getTextWidth(redEyeText) + 6;
      pdf.roundedRect(badgeStartX + durationWidth + 4, y + 3, redEyeWidth, 7, 1, 1, 'F');
      pdf.text(redEyeText, badgeStartX + durationWidth + 7, y + 8);
    }
    
    pdf.setTextColor(...MUTED_COLOR);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(trip.destinationName, margin + 5, y + 15);
    
    // Flight times - cleaner layout
    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(8);
    pdf.text(`Depart: ${trip.departureDate}, ${trip.departureTime}`, margin + 5, y + 21);
    pdf.text(`Arrive: ${trip.landingDate}, ${trip.landingTime} ${trip.destinationTimezone}`, margin + 75, y + 21);
    
    y += 30;

    // Timeline section
    pdf.setFillColor(...LIGHT_BG);
    pdf.roundedRect(margin, y, contentWidth, 6, 1, 1, 'F');
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(trip.landingDate, margin + 3, y + 4);
    y += 10;

    // Timeline items
    pdf.setDrawColor(...BRAND_COLOR);
    pdf.setLineWidth(1);
    pdf.line(margin + 3, y, margin + 3, y + (trip.timeline.length * 8));

    trip.timeline.forEach(item => {
      y = checkPageBreak(pdf, y, margin, 8);
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      
      if (item.isHighlight) {
        pdf.setFillColor(254, 243, 199);
        pdf.roundedRect(margin + 6, y - 2, contentWidth - 10, 8, 1, 1, 'F');
        pdf.setTextColor(...BRAND_COLOR);
        pdf.setFont('helvetica', 'bold');
      } else {
        pdf.setTextColor(item.isMuted ? MUTED_COLOR[0] : TEXT_COLOR[0], 
                         item.isMuted ? MUTED_COLOR[1] : TEXT_COLOR[1],
                         item.isMuted ? MUTED_COLOR[2] : TEXT_COLOR[2]);
      }
      
      pdf.text(item.time, margin + 8, y + 3);
      pdf.text(item.description, margin + 25, y + 3);
      
      y += 7;
    });
    y += 8;

    // Return info
    pdf.setFillColor(...LIGHT_BG);
    pdf.roundedRect(margin, y, contentWidth, 10, 1, 1, 'F');
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text('Return:', margin + 3, y + 4);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${trip.returnDate}, earliest departure ${trip.returnTime} ${trip.destinationTimezone}`, margin + 22, y + 4);
    y += 16;
  });

  // Footer
  y = pdf.internal.pageSize.getHeight() - 12;
  drawLine(pdf, margin, y - 3, pageWidth - margin);
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.text(`Generated by The Lost Strings Company on ${format(new Date(), 'MMMM d, yyyy')}`, pageWidth / 2, y, { align: 'center' });

  return pdf.output('blob');
}

// Base64 conversion helper
export async function pdfBlobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
